/**
 * use-request-tasks.ts
 * 
 * Hook per la gestione dei task associati alle richieste.
 * Collega il modulo Requests al modulo Board/Kanban.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCacheConfig } from '../contexts/cache-config-context';
import { requestsNew, tasksNew } from '../services/api';
import { ProjectTask, ProjectTaskCreateRequest, ProjectTaskUpdateRequest, TaskStatus } from '../types/api';
import { isTaskCompleted } from '../utils/task-status-utils';

export function useRequestTasks(requestId: string) {
  const { config } = useCacheConfig();
  const queryClient = useQueryClient();

  const tasksQuery = useQuery({
    queryKey: ['request-tasks', requestId],
    queryFn: () => requestsNew.getRequestTasks(requestId),
    enabled: !!requestId,
    staleTime: config.tasks,
    gcTime: config.tasks * 2,
  });

  const createTaskMutation = useMutation({
    mutationFn: (taskData: ProjectTaskCreateRequest) => 
      requestsNew.createRequestTask(requestId, taskData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['request-tasks', requestId] });
      queryClient.invalidateQueries({ queryKey: ['requests', requestId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: ProjectTaskUpdateRequest }) =>
      tasksNew.updateTask(taskId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['request-tasks', requestId] });
      queryClient.invalidateQueries({ queryKey: ['requests', requestId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (taskId: string) => tasksNew.deleteTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['request-tasks', requestId] });
      queryClient.invalidateQueries({ queryKey: ['requests', requestId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const updateTaskStatusMutation = useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: TaskStatus }) =>
      tasksNew.updateTaskStatus(taskId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['request-tasks', requestId] });
      queryClient.invalidateQueries({ queryKey: ['requests', requestId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  return {
    // Queries
    tasks: tasksQuery.data,
    isLoading: tasksQuery.isLoading,
    error: tasksQuery.error,
    isError: tasksQuery.isError,
    refetch: tasksQuery.refetch,

    // Mutations
    createTaskMutation,
    updateTaskMutation,
    deleteTaskMutation,
    updateTaskStatusMutation,

    // Mutation states
    isCreating: createTaskMutation.isPending,
    isUpdating: updateTaskMutation.isPending,
    isDeleting: deleteTaskMutation.isPending,
    isUpdatingStatus: updateTaskStatusMutation.isPending,
  };
}

export function useRequestTasksStats(requestId: string) {
  const { config } = useCacheConfig();

  return useQuery({
    queryKey: ['request-tasks-stats', requestId],
    queryFn: async () => {
      const tasks = await requestsNew.getRequestTasks(requestId);
      // t.status è l'enum numerico: confrontarlo con .toString() a stringhe come 'ToDo'
      // non ha mai funzionato. Si usa statusString/isTaskCompleted, coerenti col nuovo
      // modello Iniziato/Completato (gli stati intermedi sono ormai tutti "Doing").
      const stats = {
        total: tasks.length,
        todo: tasks.filter((t: ProjectTask) => t.statusString === 'ToDo').length,
        inProgress: tasks.filter((t: ProjectTask) => !isTaskCompleted(t)).length,
        done: tasks.filter((t: ProjectTask) => isTaskCompleted(t)).length,
        completionRate: tasks.length > 0 ? (tasks.filter((t: ProjectTask) => isTaskCompleted(t)).length / tasks.length) * 100 : 0,
      };

      return stats;
    },
    enabled: !!requestId,
    staleTime: config.tasks,
    gcTime: config.tasks * 2,
  });
}

export function useCreateRequestTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, taskData }: { requestId: string; taskData: ProjectTaskCreateRequest }) =>
      requestsNew.createRequestTask(requestId, taskData),
    onSuccess: (_, { requestId }) => {
      queryClient.invalidateQueries({ queryKey: ['request-tasks', requestId] });
      queryClient.invalidateQueries({ queryKey: ['requests', requestId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useTasksForRequest(requestId: string) {
  const { config } = useCacheConfig();

  return useQuery({
    queryKey: ['tasks', 'request', requestId],
    queryFn: () => tasksNew.getTasksByRequest(requestId),
    enabled: !!requestId,
    staleTime: config.tasks,
    gcTime: config.tasks * 2,
  });
}
